import {useEffect, useRef} from 'react'
import {useMutation, useQuery, useQueryClient, type QueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'
import {
  mezclarDecididos,
  unirTramo,
  type ColaRead,
  type DecisionCola,
  type ResumenCola,
  type TarjetaCola,
} from '@/lib/bateria-cola'
import type {FiltroCola} from '@/lib/cola-filtros'
import {
  useEvaluacionInvalidation,
  type BateriaEvaluacionDetail as EvaluacionDetalle,
  type BateriaPaquete,
} from '@/hooks/bateria/use-bateria'
export {useValidarLote, useRevisionResumen as useResumenRevision} from '@/hooks/bateria/use-bateria'
export type {ValidacionMasiva} from '@/hooks/bateria/use-bateria'

const base = (businessId: string) => `/admin/businesses/${businessId}/bateria`

export type DecisionesAplicadas = components['schemas']['DecisionesRead']

/** Cada tarjeta cuesta ~2 firmas IAM de ~108 ms (medido): doce ≈ 2 s hasta la primera tarjeta. */
const TRAMO_PRIMERO = 12
/** Tope del router: `LIMIT_MAX` = 500. */
const TRAMO_SIGUIENTE = 200
/** Techo contra un cursor que no avanza; al alcanzarlo la cola se declara recortada. */
const MAX_TARJETAS = 10_000

/** Todo filtro que cambie la lista va en la llave: los tramos se apilan sobre ella (`unirTramo`) y dos
 * filtros con la misma llave concatenarían listas distintas. */
function claveDeCola(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  filtro: FiltroCola,
  desde: string | null
) {
  return [
    'bateria-cola',
    businessId,
    evaluacionId,
    filtro.paqueteIds ?? null,
    filtro.solo ?? null,
    filtro.alcance ?? null,
    filtro.estados ?? null,
    filtro.confianzaMax ?? null,
    // La «segunda vuelta» es otra cola congelada, con llave propia.
    desde,
  ]
}

async function pedirTramo(
  businessId: string,
  evaluacionId: string,
  filtro: FiltroCola,
  cursor: string | null,
  limit: number
): Promise<ColaRead> {
  const resp = await api.get<ColaRead>(`${base(businessId)}/evaluaciones/${evaluacionId}/revision/cola`, {
    params: {
      limit,
      cursor,
      paquete_id: filtro.paqueteIds,
      solo: filtro.solo,
      alcance: filtro.alcance,
      estado: filtro.estados,
      confianza_max: filtro.confianzaMax ?? undefined,
    },
    // `paquete_id` repetido, nunca `paquete_id[]=`: FastAPI no despacha los corchetes de axios a la lista.
    paramsSerializer: {indexes: null},
  })
  return resp.data
}

/** Para el refresco de decididos, donde una respuesta parcial se leería como «nadie lo decidió». */
async function pedirColaCompleta(
  businessId: string,
  evaluacionId: string,
  filtro: FiltroCola,
  desde: string | null
): Promise<TarjetaCola[]> {
  const tarjetas: TarjetaCola[] = []
  let cursor: string | null = desde
  do {
    const tramo: ColaRead = await pedirTramo(businessId, evaluacionId, filtro, cursor, TRAMO_SIGUIENTE)
    tarjetas.push(...tramo.tarjetas)
    cursor = tramo.next_cursor
  } while (cursor !== null && tarjetas.length < MAX_TARJETAS)
  return tarjetas
}

export type ColaEnTramos = {
  data: ColaRead | undefined
  /** Ni la primera tarjeta llegó todavía. */
  isLoading: boolean
  /** Siguen llegando tramos; la pantalla ya es usable. */
  cargandoTramos: boolean
  /** Se alcanzó el techo con el cursor todavía vivo. */
  recortada: boolean
  /** El primer tramo agotó sus reintentos. */
  error: boolean
  /** Reintenta el primer tramo. */
  reintentar: () => void
  /** Un tramo de atrás agotó sus reintentos y la cadena se detuvo. */
  errorTramos: boolean
  /** Reintenta el tramo estancado con el mismo cursor. */
  reintentarTramos: () => void
}

/** Congelada: un refetch reordenaría la lista bajo el cursor, y lo nuevo va a la «segunda vuelta» (v2 §1.7).
 * Los tramos se apilan al final: el cursor es un ordinal sobre el orden que el servidor fijó una vez. */
export function useColaRevision(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  filtro: FiltroCola,
  enabled: boolean,
  desde?: string | null
): ColaEnTramos {
  const query = useQuery({
    queryKey: claveDeCola(businessId, evaluacionId, filtro, desde ?? null),
    queryFn: () => pedirTramo(businessId!, evaluacionId!, filtro, desde ?? null, TRAMO_PRIMERO),
    enabled: enabled && !!businessId && !!evaluacionId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
  useRefrescoDeDecididos(businessId, evaluacionId, filtro, desde ?? null, query.dataUpdatedAt)
  const cargadas = query.data?.tarjetas.length ?? 0
  const faltan = (query.data?.next_cursor ?? null) !== null
  const hayMasTramos = faltan && cargadas < MAX_TARJETAS
  const tramos = useTramosDeFondo(
    businessId,
    evaluacionId,
    filtro,
    desde ?? null,
    hayMasTramos ? query.data!.next_cursor : null
  )
  return {
    data: query.data,
    isLoading: query.isLoading,
    cargandoTramos: hayMasTramos && !tramos.error,
    recortada: faltan && cargadas >= MAX_TARJETAS,
    error: query.isError,
    reintentar: () => void query.refetch(),
    errorTramos: hayMasTramos && tramos.error,
    reintentarTramos: tramos.reintentar,
  }
}

/** Encadenados: al apilar un tramo cambia `next_cursor` y sale el siguiente; agotados los reintentos la cadena
 * se detiene hasta `reintentar`. `gcTime: 0` porque cada tramo solo sirve al apilarse. */
function useTramosDeFondo(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  filtro: FiltroCola,
  desde: string | null,
  cursor: string | null
): {error: boolean; reintentar: () => void} {
  const queryClient = useQueryClient()
  const tramo = useQuery({
    queryKey: [...claveDeCola(businessId, evaluacionId, filtro, desde), 'tramo', cursor],
    queryFn: () => pedirTramo(businessId!, evaluacionId!, filtro, cursor, TRAMO_SIGUIENTE),
    enabled: cursor !== null && !!businessId && !!evaluacionId,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
  const llegado = tramo.data
  useEffect(() => {
    if (llegado === undefined) return
    queryClient.setQueryData(claveDeCola(businessId, evaluacionId, filtro, desde), (previa: ColaRead | undefined) =>
      previa === undefined ? previa : unirTramo(previa, llegado)
    )
    // Si el filtro cambia, cambia la llave y `llegado` vuelve a `undefined`.
  }, [llegado]) // eslint-disable-line react-hooks/exhaustive-deps
  return {error: tramo.isError, reintentar: () => void tramo.refetch()}
}

/** Al montar desde caché trae qué tarjetas ya decidió alguien: `alcance=todo` es el único que manda `decidido`,
 * y se mezcla por ref para no reordenar la cola ni perder sus URLs firmadas. */
function useRefrescoDeDecididos(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  filtro: FiltroCola,
  desde: string | null,
  dataUpdatedAt: number
): void {
  const queryClient = useQueryClient()
  const montaje = useRef(Date.now())
  const deCache = dataUpdatedAt > 0 && dataUpdatedAt < montaje.current
  const refresco = useQuery({
    queryKey: [
      'bateria-cola-decididos',
      businessId,
      evaluacionId,
      filtro.paqueteIds ?? null,
      filtro.solo ?? null,
      filtro.alcance ?? null,
      filtro.estados ?? null,
      filtro.confianzaMax ?? null,
      desde,
    ],
    queryFn: async () => {
      const frescas = await pedirColaCompleta(
        businessId!,
        evaluacionId!,
        {...filtro, alcance: 'todo', estados: ['decididos']},
        desde
      )
      // Solo (ref, decidido): las tarjetas enteras ya están en la caché de la cola.
      return frescas.filter((t) => t.decidido).map((t) => ({ref: t.ref, decidido: t.decidido}))
    },
    enabled: deCache,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
  const frescas = refresco.data
  useEffect(() => {
    if (frescas === undefined) return
    queryClient.setQueryData(claveDeCola(businessId, evaluacionId, filtro, desde), (previa: ColaRead | undefined) => {
      if (previa === undefined) return previa
      const tarjetas = mezclarDecididos(previa.tarjetas, frescas)
      // Sin novedades no se toca la caché: un objeto nuevo re-sembraría el estado de la pantalla.
      return tarjetas === previa.tarjetas ? previa : {...previa, tarjetas}
    })
    // Si el filtro cambia, cambia la llave del refresco y `frescas` vuelve a `undefined`.
  }, [frescas]) // eslint-disable-line react-hooks/exhaustive-deps
}

/** `alcance=todo` cuenta también lo decidido; comparte raíz de llave con el resumen para heredar sus invalidaciones. */
export function useResumenDeTodo(businessId: string, evaluacionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-revision-resumen', businessId, evaluacionId, 'todo'],
    queryFn: async () =>
      (
        await api.get<ResumenCola>(`${base(businessId)}/evaluaciones/${evaluacionId}/revision/resumen`, {
          params: {alcance: 'todo'},
        })
      ).data,
    enabled,
    staleTime: Infinity,
  })
}

/** Idempotente por `(tipo, ref)`: la cola de reintento puede re-mandar sin duplicar ni perder. */
export function useAplicarDecisiones(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (decisiones: DecisionCola[]) =>
      (
        await api.post<DecisionesAplicadas>(`${base(businessId)}/evaluaciones/${evaluacionId}/revision/decisiones`, {
          decisiones,
        })
      ).data,
    // `use-envio-decisiones.ts` pinta su propio banner de reintento; sin esto la red global duplica aviso y
    // reporte a Sentry (PYTHON-2P).
    meta: {manejaError: true},
    // La cola congelada no se invalida. La evaluación (~350 KB sin paginar con 500 personas) recibe un delta
    // en caché y se pide una sola vez cuando para el tecleo.
    onSuccess: (data, decisiones) => {
      queryClient.invalidateQueries({queryKey: ['bateria-revision-resumen', businessId, evaluacionId]})
      aplicarDeltaDePendientes(queryClient, businessId, evaluacionId, decisiones, data)
      programarInvalidacion(queryClient, businessId, evaluacionId)
    },
  })
}

/** Muy por encima de la cadencia de envío (~600 ms): una sola descarga al final de cada ráfaga. */
const INVALIDAR_TRAS_MS = 5_000

const temporizadores = new Map<string, ReturnType<typeof setTimeout>>()

function programarInvalidacion(queryClient: QueryClient, businessId: string, evaluacionId: string): void {
  const clave = `${businessId}:${evaluacionId}`
  const pendiente = temporizadores.get(clave)
  if (pendiente !== undefined) clearTimeout(pendiente)
  temporizadores.set(
    clave,
    setTimeout(() => {
      temporizadores.delete(clave)
      queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
    }, INVALIDAR_TRAS_MS)
  )
}

/** Aproximación hasta `programarInvalidacion`: solo marcas, sin conflictos (ya restados en el servidor), y piso 0
 * para que un reintento idempotente no deje el número negativo. */
function aplicarDeltaDePendientes(
  queryClient: QueryClient,
  businessId: string,
  evaluacionId: string,
  decisiones: DecisionCola[],
  aplicadas: DecisionesAplicadas
): void {
  const enConflicto = new Set(aplicadas.conflictos.map((c) => c.ref))
  const porPaquete = new Map<string, number>()
  for (const decision of decisiones) {
    if (decision.tipo !== 'marca' || enConflicto.has(decision.ref)) continue
    const paqueteId = decision.ref.split(':')[1]
    if (paqueteId) porPaquete.set(paqueteId, (porPaquete.get(paqueteId) ?? 0) + 1)
  }
  if (porPaquete.size === 0) return
  queryClient.setQueryData(['bateria-evaluacion', businessId, evaluacionId], (previo: EvaluacionDetalle | undefined) =>
    previo === undefined
      ? previo
      : {
          ...previo,
          paquetes: previo.paquetes.map((paquete) =>
            porPaquete.has(paquete.id)
              ? {
                  ...paquete,
                  pendientes_de_decision: Math.max(
                    0,
                    paquete.pendientes_de_decision - (porPaquete.get(paquete.id) ?? 0)
                  ),
                }
              : paquete
          ),
        }
  )
}

/** Devuelve el paquete a `transcrito`: retira la calificación, no la revisión guardada. */
export function useQuitarValidacion(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async (paqueteId: string) =>
      (
        await api.post<BateriaPaquete>(
          `/admin/businesses/${businessId}/bateria/paquetes/${paqueteId}/quitar-validacion`,
          {}
        )
      ).data,
    onSuccess: invalidate,
  })
}

export type ImportRevision = components['schemas']['ImportRevisionRead']

/** G31. Ancla temporal y no `window.open`, que abriría una pestaña en blanco; `paquete_ids` va sin corchetes,
 * como en la cola. */
export function useExportRevisionXlsx(businessId: string, evaluacionId: string) {
  return useMutation({
    mutationFn: async (paqueteIds?: string[]) => {
      const resp = await api.get(`${base(businessId)}/evaluaciones/${evaluacionId}/revision/export.xlsx`, {
        params: {paquete_ids: paqueteIds?.length ? paqueteIds : undefined},
        paramsSerializer: {indexes: null},
        responseType: 'blob',
      })
      const objectUrl = URL.createObjectURL(resp.data as Blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = `revision-${evaluacionId}.xlsx`
      anchor.click()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    },
  })
}

/** G31: las `ignoradas` se muestran siempre; una fila que no aplica en silencio pierde decisiones. */
export function useImportRevisionXlsx(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return (await api.post<ImportRevision>(`${base(businessId)}/evaluaciones/${evaluacionId}/revision/import`, form))
        .data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['bateria-revision-resumen', businessId, evaluacionId]})
      queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
      queryClient.invalidateQueries({queryKey: ['bateria-cola', businessId, evaluacionId]})
    },
  })
}
